import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("tembo_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && !window.location.pathname.includes("/login")) {
      localStorage.removeItem("tembo_token");
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

// ── Auth ──────────────────────────────────────────────────────────────────────
export const authApi = {
  login: (email, password) => api.post("/auth/login", { email, password }),
  me:    ()               => api.get("/auth/me"),
};

// ── Stats ─────────────────────────────────────────────────────────────────────
export const statsApi = {
  get: () => api.get("/stats"),
};

// ── Snapshots ─────────────────────────────────────────────────────────────────
export const snapshotsApi = {
  list:     (params)        => api.get("/snapshots", { params }),
  search:   (q)             => api.get("/snapshots/search", { params: { q } }),
  get:      (id)            => api.get(`/snapshots/${id}`),
  create:   (data)          => api.post("/snapshots", data),
  versions: (id)            => api.get(`/snapshots/${id}/versions`),
  rate:     (id, data)      => api.patch(`/snapshots/${id}/rate`, data),
};

// ── Hermes inbox ──────────────────────────────────────────────────────────────
export const inboxApi = {
  list:    ()   => api.get("/hermes-inbox"),
  elevate: (id) => api.post(`/hermes-inbox/${id}/elevate`),
};

// ── Discussion ────────────────────────────────────────────────────────────────
export const discussionApi = {
  list: (sid)       => api.get(`/snapshots/${sid}/discussion`),
  post: (sid, data) => api.post(`/snapshots/${sid}/discussion`, data),
};

// ── Counter-hypotheses ────────────────────────────────────────────────────────
export const countersApi = {
  list:   (sid)            => api.get(`/snapshots/${sid}/counter-hypotheses`),
  create: (sid, data)      => api.post(`/snapshots/${sid}/counter-hypotheses`, data),
  update: (sid, cid, data) => api.patch(`/snapshots/${sid}/counter-hypotheses/${cid}`, data),
};

// ── Working theories ──────────────────────────────────────────────────────────
export const theoriesApi = {
  list:   (sid)       => api.get(`/snapshots/${sid}/working-theories`),
  latest: (sid)       => api.get(`/snapshots/${sid}/working-theories/latest`),
  create: (sid, data) => api.post(`/snapshots/${sid}/working-theories`, data),
};

// ── Branches ──────────────────────────────────────────────────────────────────
export const branchesApi = {
  list:   (sid)       => api.get(`/snapshots/${sid}/branches`),
  create: (sid, data) => api.post(`/snapshots/${sid}/branches`, data),
};

// ── Learnings ─────────────────────────────────────────────────────────────────
export const learningsApi = {
  list:    ()           => api.get("/learnings"),
  promote: (data)       => api.post("/learnings", data),
  update:  (id, data)   => api.patch(`/learnings/${id}`, data),
  demote:  (id)         => api.delete(`/learnings/${id}`),
};

// ── Bookmarks (Vault) ─────────────────────────────────────────────────────────
export const bookmarksApi = {
  snapshots: ()            => api.get("/bookmarks/snapshots"),
  add:       (snapshot_id) => api.post("/bookmarks", { snapshot_id }),
  remove:    (snapshot_id) => api.delete(`/bookmarks/${snapshot_id}`),
};

// ── Entities ──────────────────────────────────────────────────────────────────
export const entitiesApi = {
  list:   (q)    => api.get("/entities", { params: q ? { q } : {} }),
  get:    (id)   => api.get(`/entities/${id}`),
  create: (data) => api.post("/entities", data),
};

// ── Sources ───────────────────────────────────────────────────────────────────
export const sourcesApi = {
  list:   (params) => api.get("/sources", { params }),
  create: (data)   => api.post("/sources", data),
  update: (id, d)  => api.put(`/sources/${id}`, d),
  delete: (id)     => api.delete(`/sources/${id}`),
};

// ── Feeds ─────────────────────────────────────────────────────────────────────
export const feedsApi = {
  list:   ()     => api.get("/feeds"),
  create: (data) => api.post("/feeds", data),
};

// ── Decisions ─────────────────────────────────────────────────────────────────
export const decisionsApi = {
  list:   (params) => api.get("/decisions", { params }),
  get:    (id)     => api.get(`/decisions/${id}`),
  create: (data)   => api.post("/decisions", data),
  update: (id, d)  => api.patch(`/decisions/${id}`, d),
};

// ── Hermes (tasks, suggestions, config, conversations) ────────────────────────
export const hermesApi = {
  tasks: {
    list:   ()         => api.get("/hermes/tasks"),
    create: (data)     => api.post("/hermes/tasks", data),
    update: (id, data) => api.patch(`/hermes/tasks/${id}`, data),
  },
  suggestions: {
    list:    (params)  => api.get("/suggestions", { params }),
    create:  (data)    => api.post("/suggestions", data),
    update:  (id, data)=> api.patch(`/suggestions/${id}`, data),
  },
  config: {
    get:      (file)        => api.get(`/hermes/config/${file}`),
    save:     (file, data)  => api.put(`/hermes/config/${file}`, data),
    version:  (file, vid)   => api.get(`/hermes/config/${file}/versions/${vid}`),
    rollback: (file, vid)   => api.post(`/hermes/config/${file}/rollback/${vid}`),
  },
  conversations: {
    list:     ()            => api.get("/hermes/conversations"),
    create:   (data)        => api.post("/hermes/conversations", data),
    rename:   (id, data)    => api.patch(`/hermes/conversations/${id}`, data),
    delete:   (id)          => api.delete(`/hermes/conversations/${id}`),
    messages: (id)          => api.get(`/hermes/conversations/${id}/messages`),
    send:     (id, data)    => api.post(`/hermes/conversations/${id}/messages`, data),
  },
};

// ── Hub summary ───────────────────────────────────────────────────────────────
export const hubApi = {
  summary: () => api.get("/hub"),
};

// ── Hermes live status ────────────────────────────────────────────────────────
export const hermesStatusApi = {
  get: () => api.get("/hermes/agent/status"),
};

export { api };
export default api;
